import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Grid,
  Button,
  IconButton,
  FormHelperText,
  Paper,
  TextField,
  Box,
  Typography,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AssistantSharpIcon from "@mui/icons-material/AssistantSharp";

const QC_BACKEND_BASE_URL = "https://server-9p4hx.kinsta.app";
const QC_API_BASE_URL = `${QC_BACKEND_BASE_URL}/api/qc`;
const TASKS_API_BASE_URL = `${QC_BACKEND_BASE_URL}/api/tasks`;
const MAX_QC_IMAGES = 12;
const QC_IMAGE_SLOT_INDEXES = Array.from(
  { length: MAX_QC_IMAGES },
  (_, index) => index
);
const buildEmptyImageSlots = () =>
  Array.from({ length: MAX_QC_IMAGES }, () => null);

function CreareRaport({ issueId }) {
  const draftKey = `qc_report_draft_${issueId}`;
  const [selectedTask, setSelectedTask] = useState(null);
  const [priceOption, setPriceOption] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [accompaniedItems, setAccompaniedItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState(buildEmptyImageSlots);
  const [errors, setErrors] = useState({});
  const [emailStatus, setEmailStatus] = useState(null);
  const [smsStatus, setSmsStatus] = useState(null);
  const [jiraStatus, setJiraStatus] = useState(null);
  const [sellNowStatus, setSellNowStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const materialLabelsToKeys = {
    Exterior: "exterior",
    Interior: "interior",
    Forma: "shape",
    Hardware: "hardware",
    "Fermoare-Inchideri": "zippers",
    Finisaje: "finishes",
    Mirosuri: "smell",
    Talpa: "sole",
    Cute: "creases",
    Suprafețe: "surfaces",
    Pietre: "stones",
    Cadran: "dial",
    "Bratara-Curea": "strapBracelet",
    Geam: "glass",
    Spate: "caseBack",
    Mecanism: "mechanism",
  };

  // Verificăm dacă issueId este definit înainte de a face orice operațiune
  useEffect(() => {
    if (issueId) {
      fetchTaskDetailsFromBackend();
    } else {
      console.error("issueId is undefined or null");
      setErrors((prevErrors) => ({
        ...prevErrors,
        issueId: "issueId is undefined or null",
      }));
    }
  }, [issueId]);

  const fetchTaskDetailsFromBackend = async () => {
    try {
      console.log(`Fetching task details for issueId: ${issueId}`);
      const response = await axios.get(
        `${TASKS_API_BASE_URL}/jira-task/${issueId}`
      );
      console.log("Response data from JIRA:", response.data); // Printează datele primite

      if (response.data) {
        const rawCategory = response.data.customfield_10173;
        console.log("Raw Category:", rawCategory); // Consola pentru verificare

        const categoryValue =
          rawCategory && rawCategory.id ? parseInt(rawCategory.id, 10) : NaN;

        console.log("Parsed Category Value:", categoryValue); // Consola pentru verificare

        const taskDetails = {
          summary: response.data.summary,
          entryPrice: response.data.customfield_10072, // Preț negociat
          entryDate: response.data.data_de_intrare, // Data de intrare
          productCategory: productCategoryMap[categoryValue] || "N/A", // Categorie Prod (corectat)
          fullName: response.data.name, // Nume și Prenume
          email: response.data.email, // Email
          phone: response.data.phone, // Telefon
          issue_id: issueId,
          materials: {},
          authenticity: "",
          inspector: "",
          Observations: "",
          images: [], // Inițializează `images` ca un array gol
        };

        setSelectedTask(taskDetails);
        saveInitialTaskData(taskDetails);

        // Try to restore a saved draft for this issueId
        try {
          const raw = localStorage.getItem(draftKey);
          if (raw) {
            const draft = JSON.parse(raw);
            if (draft?.selectedTask)
              setSelectedTask((prev) => ({ ...prev, ...draft.selectedTask }));
            if (Array.isArray(draft?.accompaniedItems))
              setAccompaniedItems(draft.accompaniedItems);
            if (typeof draft?.priceOption === "string")
              setPriceOption(draft.priceOption);
            if (draft?.newPrice !== undefined) setNewPrice(draft.newPrice);
          }
        } catch (e) {
          console.warn("Failed to parse saved QC draft:", e);
        }
      } else {
        console.error("No data returned from backend");
      }
    } catch (error) {
      console.error(
        "Eroare la preluarea datelor din backend:",
        error.message || error
      );
    }
  };

  // Auto-save draft to localStorage on change
  useEffect(() => {
    if (!issueId) return;
    const snapshot = {
      selectedTask,
      priceOption,
      newPrice,
      accompaniedItems,
    };
    try {
      localStorage.setItem(draftKey, JSON.stringify(snapshot));
    } catch (e) {
      console.warn("Failed saving QC draft:", e);
    }
  }, [issueId, selectedTask, priceOption, newPrice, accompaniedItems]);

  const productCategoryMap = {
    10207: "Genti",
    10208: "Incaltaminte",
    10209: "Accesorii",
    10210: "Bijuterii",
    10211: "Ceasuri",
    10212: "Imbracaminte",
  };

  const saveInitialTaskData = async (taskDetails) => {
    try {
      await axios.post(
        `${QC_API_BASE_URL}/save-quality-check`,
        {
          TaskID: taskDetails.issue_id,
          Summary: taskDetails.summary,
          EntryPrice: taskDetails.entryPrice, // Folosește direct valoarea din taskDetails
          EntryDate: taskDetails.entryDate, // Folosește direct valoarea din taskDetails
          ProductCategory: taskDetails.productCategory, // Folosește direct valoarea din taskDetails
          FullName: taskDetails.fullName, // Folosește direct valoarea din taskDetails
          Email: taskDetails.email, // Folosește direct valoarea din taskDetails
          Phone: taskDetails.phone, // Folosește direct valoarea din taskDetails
          Status: "New",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Task-ul a fost salvat cu succes.");
    } catch (error) {
      console.error(
        "Eroare la salvarea task-ului:",
        error.response?.data?.message || error.message
      );
    }
  };

  const handleMaterialChange = (prop) => (event) => {
    const updatedMaterials = {
      ...selectedTask.materials,
      [prop]: event.target.value.match(/^([1-9]|10)$/)
        ? event.target.value
        : "",
    };

    setSelectedTask((prevState) => ({
      ...prevState,
      materials: updatedMaterials,
    }));
  };

  const handleItemChange = (index) => (event) => {
    const updatedAccompaniedItems = [...accompaniedItems];
    updatedAccompaniedItems[index] = event.target.value;
    setAccompaniedItems(updatedAccompaniedItems);
    setSelectedTask((prevState) => ({
      ...prevState,
      accompaniedItems: JSON.stringify(updatedAccompaniedItems),
    }));
  };

  const handleDeleteItem = (index) => {
    const updatedAccompaniedItems = [...accompaniedItems];
    updatedAccompaniedItems.splice(index, 1);
    setAccompaniedItems(updatedAccompaniedItems);
    setSelectedTask((prevState) => ({
      ...prevState,
      accompaniedItems: JSON.stringify(updatedAccompaniedItems),
    }));
  };

  const handleAddItem = () => {
    if (newItem.trim() !== "") {
      const updatedAccompaniedItems = [...accompaniedItems, newItem];
      setAccompaniedItems(updatedAccompaniedItems);
      setSelectedTask((prevState) => ({
        ...prevState,
        accompaniedItems: updatedAccompaniedItems,
      }));
      setNewItem("");
    }
  };

  const handlePriceOptionChange = (event) => {
    const updatedPriceOption = event.target.value;
    setPriceOption(updatedPriceOption);
    setSelectedTask((prevState) => ({
      ...prevState,
      priceOption: updatedPriceOption,
    }));
    if (updatedPriceOption !== "Propunere pret nou") {
      setNewPrice("");
      setSelectedTask((prevState) => ({
        ...prevState,
        newPrice: null,
      }));
    }
  };

  const handleNewPriceChange = (event) => {
    const updatedNewPrice = event.target.value;
    setNewPrice(updatedNewPrice);
    setSelectedTask((prevState) => ({
      ...prevState,
      newPrice: updatedNewPrice ? parseFloat(updatedNewPrice) : null,
    }));
  };

  const handleObservationChange = (event) => {
    const updatedObservation = event.target.value;
    setSelectedTask((prevState) => ({
      ...prevState,
      Observations: updatedObservation,
    }));
  };

  const handleImageChange = (index) => async (event) => {
    const file = event.target.files[0];
    if (file) {
      const localImageUrl = URL.createObjectURL(file);

      setImageFiles((prevFiles) => {
        const newFiles = [...prevFiles];
        newFiles[index] = {
          previewUrl: localImageUrl,
          serverPath: "",
        };
        return newFiles;
      });

      try {
        const formData = new FormData();
        formData.append("image", file);

        const response = await axios.post(
          `${QC_API_BASE_URL}/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        setImageFiles((prevFiles) => {
          const newFiles = [...prevFiles];
          newFiles[index] = {
            ...newFiles[index],
            serverPath: response.data.filePath,
          };
          return newFiles;
        });

        setSelectedTask((prevTask) => ({
          ...prevTask,
          images: [...(prevTask.images || []), response.data.filePath], // Folosește un array gol dacă `images` este null/undefined
        }));
      } catch (error) {
        console.error("Eroare la încărcarea imaginii:", error);
        setImageFiles((prevFiles) =>
          prevFiles.map((item, idx) => (idx === index ? null : item))
        );
      }
    }
  };

  const handleRemoveImage = async (index) => {
    const imageToDelete = imageFiles[index];
    if (!imageToDelete) return;

    try {
      await axios.post(`${QC_API_BASE_URL}/delete-image`, {
        imagePath: imageToDelete.serverPath,
      });

      setImageFiles((prevFiles) =>
        prevFiles.map((file, idx) => (idx === index ? null : file))
      );
      URL.revokeObjectURL(imageToDelete.previewUrl);

      setSelectedTask((prevTask) => ({
        ...prevTask,
        images: prevTask.images.filter((_, i) => i !== index),
      }));
    } catch (error) {
      console.error("Eroare la ștergerea imaginii de pe server:", error);
    }
  };

  const handleRepaintedChange = (event) => {
    setSelectedTask((prevState) => ({
      ...prevState,
      repainted: event.target.value,
    }));
  };

  const handleRepairedChange = (event) => {
    setSelectedTask((prevState) => ({
      ...prevState,
      repaired: event.target.value,
    }));
  };

  const handleSubmit = async (desiredStatus = "Done") => {
    // Validate fields before submitting data
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    // Format entry date and filter valid image files
    const formattedEntryDate = selectedTask.entryDate
      ? new Date(selectedTask.entryDate).toISOString().slice(0, 10)
      : null;
    const filteredImageFiles = imageFiles
      .filter((file) => file != null && file.serverPath != null)
      .map((file) => file.serverPath);

    // Calculate total points and general condition
    const { total, condition } = calculateTotalAndCondition();

    // Prepare the data object to be sent to the backend
    const updatedTask = {
      TaskID: selectedTask.issue_id,
      PriceOption: priceOption,
      NewPrice: newPrice ? parseFloat(newPrice) : null,
      Authenticity: selectedTask.authenticity,
      AccompaniedItems: JSON.stringify(accompaniedItems),
      Observations: selectedTask.Observations,
      Puncte: total,
      Conditie: condition,
      Images: JSON.stringify(filteredImageFiles),
      Materials: JSON.stringify(selectedTask.materials),
      Repainted: selectedTask.repainted,
      Repaired: selectedTask.repaired,
      Summary: selectedTask.summary,
      EntryPrice: selectedTask.entryPrice,
      EntryDate: formattedEntryDate,
      ProductCategory: selectedTask.productCategory,
      FullName: selectedTask.fullName,
      Email: selectedTask.email,
      Phone: selectedTask.phone,
      CurrentStatus: "Done",
      DesiredStatus: desiredStatus,
      Data: new Date().toISOString().slice(0, 10),
      Hide: "Nu",
      Inspector: selectedTask.inspector,
    };

    console.log("Data sent to backend:", JSON.stringify(updatedTask, null, 2));

    setEmailStatus(null);
    setSmsStatus(null);
    setJiraStatus(null);
    setSellNowStatus(null);

    try {
      // Save data to the database
      const saveResponse = await axios.post(
        `${QC_API_BASE_URL}/save-quality-check`,
        updatedTask
      );

      if (saveResponse.status === 200) {
        console.log("Task saved successfully.");
        try {
          localStorage.removeItem(draftKey);
        } catch (e) {}

        // Generate quality report and send email
        try {
          const reportResponse = await axios.post(
            `${QC_API_BASE_URL}/generate-quality-report`,
            {
              selectedTask: updatedTask,
            }
          );

          if (reportResponse.status === 200) {
            console.log(reportResponse.data);

            setEmailStatus({
              severity: "success",
              message: "Emailul a fost trimis cu succes către client.",
            });

            setJiraStatus({
              severity: "success",
              message:
                reportResponse.data?.message ||
                "Raportul a fost încărcat cu succes în JIRA.",
            });

            const sellNowSync = reportResponse.data?.sellNowQualityCheckSync;
            console.log(
              "[CreareRaportQC] sellNowQualityCheckSync:",
              sellNowSync || null
            );
            if (sellNowSync) {
              const upstreamProductId =
                sellNowSync.productId || sellNowSync.upstreamData?.id;
              const upstreamStatus =
                sellNowSync.upstreamData?.status || null;
              const detailParts = [
                upstreamProductId
                  ? `Product ID: ${upstreamProductId}`
                  : null,
                upstreamStatus ? `Status: ${upstreamStatus}` : null,
              ].filter(Boolean);
              const detailsSuffix = detailParts.length
                ? ` (${detailParts.join(" | ")})`
                : "";

              if (sellNowSync.error) {
                setSellNowStatus({
                  severity: "error",
                  message:
                    (sellNowSync.message ||
                      "A apărut o eroare la trimiterea quality check-ului către Sell Now.") +
                    detailsSuffix,
                });
              } else if (sellNowSync.skipped) {
                const skippedMessages = {
                  not_attempted:
                    "Sincronizarea Sell Now nu a fost inițiată.",
                  status_not_qualitycheck:
                    "Sincronizarea Sell Now a fost omisă: produsul nu este în statusul corect pentru Quality Check.",
                  missing_product_id:
                    "Sincronizarea Sell Now a fost omisă: lipsește productId-ul produsului.",
                  missing_admin_price:
                    "Sincronizarea Sell Now a fost omisă: lipsește prețul administrativ.",
                  missing_sell_now_api_key:
                    "Sincronizarea Sell Now a fost omisă: lipsește cheia de integrare de pe server.",
                };

                setSellNowStatus({
                  severity: "info",
                  message:
                    sellNowSync.message ||
                    skippedMessages[sellNowSync.reason] ||
                    "Sincronizarea Sell Now a fost omisă.",
                });
              } else {
                setSellNowStatus({
                  severity: "success",
                  message:
                    (sellNowSync.message ||
                      "Quality check-ul a fost trimis cu succes către Sell Now.") +
                    detailsSuffix,
                });
              }
            }

            // Send SMS to the client
            try {
              const smsResponse = await axios.post(
                `${TASKS_API_BASE_URL}/send-quality-check-sms`,
                {
                  contactName: selectedTask.fullName,
                  contactPhone: selectedTask.phone,
                }
              );

              if (smsResponse.status === 200) {
                console.log("Quality check SMS sent successfully");
                setSmsStatus({
                  severity: "success",
                  message: "SMS-ul a fost trimis cu succes către client.",
                });
              } else {
                console.error(
                  "Failed to send quality check SMS:",
                  smsResponse.statusText
                );
                setSmsStatus({
                  severity: "error",
                  message: `Trimiterea SMS-ului a eșuat: ${smsResponse.statusText || "status neașteptat"}.`,
                });
              }
            } catch (smsError) {
              console.error(
                "Error sending quality check SMS:",
                smsError.message || smsError
              );
              const smsErrorMessage =
                smsError?.response?.data?.message ||
                smsError?.message ||
                "A apărut o eroare neașteptată.";
              setSmsStatus({
                severity: "error",
                message: `Trimiterea SMS-ului a eșuat: ${smsErrorMessage}`,
              });
            }
          } else {
            setEmailStatus({
              severity: "error",
              message: "Generarea raportului de calitate nu a reușit.",
            });
          }
        } catch (error) {
          console.error(
            "Error generating report or sending email:",
            error.response?.data?.message || error.message
          );
          const responseMessage =
            error?.response?.data?.message ||
            error?.message ||
            "A apărut o eroare neașteptată.";
          const responseDetails = error?.response?.data?.error;
          const emailErrorMessage = responseDetails
            ? `${responseMessage} Detalii: ${responseDetails}`
            : responseMessage;
          setEmailStatus({
            severity: "error",
            message: `Trimiterea emailului a eșuat: ${emailErrorMessage}`,
          });
          if (
            typeof emailErrorMessage === "string" &&
            emailErrorMessage.toLowerCase().includes("jira")
          ) {
            setJiraStatus({
              severity: "error",
              message: `Încărcarea în JIRA a eșuat: ${emailErrorMessage}`,
            });
          }
        }
      }
    } catch (error) {
      console.error(
        "Error saving task:",
        error.response?.data?.message || error.message
      );
    }
  };

  const handleSubmitDraft = async () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const formattedEntryDate = selectedTask.entryDate
      ? new Date(selectedTask.entryDate).toISOString().slice(0, 10)
      : null;
    const filteredImageFiles = imageFiles
      .filter((file) => file != null && file.serverPath != null)
      .map((file) => file.serverPath);

    const { total, condition } = calculateTotalAndCondition();

    const dataToSend = {
      TaskID: selectedTask.issue_id,
      PriceOption: selectedTask.priceOption,
      NewPrice: selectedTask.newPrice
        ? parseFloat(selectedTask.newPrice)
        : null,
      Authenticity: selectedTask.authenticity,
      AccompaniedItems: JSON.stringify(accompaniedItems),
      Observations: selectedTask.Observations,
      Puncte: total,
      Conditie: condition,
      Images: JSON.stringify(filteredImageFiles),
      Materials: JSON.stringify(selectedTask.materials),
      Summary: selectedTask.summary,
      EntryPrice: selectedTask.entryPrice,
      EntryDate: formattedEntryDate,
      ProductCategory: selectedTask.productCategory,
      FullName: selectedTask.fullName,
      Email: selectedTask.email,
      Phone: selectedTask.phone,
      CurrentStatus: "Ciorna", // Setează statusul la "Ciorna"
      DesiredStatus: "Draft",
      Data: new Date().toISOString().slice(0, 10),
      Hide: "Nu",
      Inspector: selectedTask.inspector,
    };

    console.log(
      "Data trimisă la backend pentru ciornă:",
      JSON.stringify(dataToSend, null, 2)
    );

    try {
      const response = await axios.post(
        `${QC_API_BASE_URL}/save-quality-check`,
        dataToSend,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        console.log(response.data.message);
        try {
          localStorage.removeItem(draftKey);
        } catch (e) {}
      }
    } catch (error) {
      console.error(
        "Eroare la salvarea ciornei:",
        error.response?.data?.message || error.message
      );
    }
  };

  const rephraseObservations = async () => {
    if (!selectedTask.Observations) return;
    setIsProcessing(true);
    try {
      const rephrasedText = await fetchRephrasedText(selectedTask.Observations);
      setSelectedTask((prevState) => ({
        ...prevState,
        Observations: rephrasedText,
      }));
    } catch (error) {
      console.error("Eroare la reformularea textului:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const fetchRephrasedText = async (text) => {
    try {
      const category = selectedTask.productCategory;
      const { condition, total, lowestScore } = calculateTotalAndCondition();
      const lowestScoreLabel =
        lowestScore !== null && lowestScore !== undefined ? lowestScore : "N/A";

      const prompt = `Reformuleaza, fara diacritice, intr-o maniera business si profesionista ca si un inspector de calitate expert de la o consignație de lux. Te rog sa nu scrii ca esti inspector de calitate, scrie direct observatia ta. Articolul este din categoria: ${category} si este in conditia: ${condition}; Nota totala: ${total}; Nota minima: ${lowestScoreLabel}; Observatii: ${text}`;

      console.log("[DEBUG] Trimit prompt la backend /openai-rephrase");
      const response = await axios.post(
        `${QC_API_BASE_URL}/openai-rephrase`,
        { prompt },
        { headers: { "Content-Type": "application/json" } }
      );

      console.log("[DEBUG] Raspuns backend rephrase:", response.data);
      return response.data && response.data.content
        ? response.data.content
        : "";
    } catch (error) {
      if (error.response) {
        console.error(
          "[ERROR] Backend rephrase a raspuns cu eroare:",
          error.response.status,
          error.response.data
        );
      } else {
        console.error(
          "[ERROR] Nu s-a putut apela backend rephrase:",
          error.message
        );
      }
      return "";
    }
  };

  const validateFields = () => {
    let newErrors = {};

    if (!selectedTask.authenticity) {
      newErrors.authenticity = "Acest câmp este obligatoriu.";
    }
    if (!selectedTask.inspector) {
      newErrors.inspector = "Alegerea unui inspector este obligatorie.";
    }
    if (!selectedTask.priceOption) {
      newErrors.priceOption =
        "Selectarea unei opțiuni de preț este obligatorie.";
    }
    if (
      selectedTask.priceOption === "Propunere pret nou" &&
      !selectedTask.newPrice
    ) {
      newErrors.newPrice =
        "Introducerea noului preț este obligatorie atunci când se propune un preț nou.";
    }
    if (!selectedTask.materials.cutie) {
      newErrors.cutie = "Câmpul Cutie este obligatoriu.";
    }
    if (!selectedTask.materials.accesorii) {
      newErrors.accesorii = "Câmpul Accesorii este obligatoriu.";
    }
    if (!selectedTask.Observations) {
      newErrors.Observations = "Acest câmp este obligatoriu.";
    }
    if (accompaniedItems.length === 0) {
      newErrors.accompaniedItems =
        "Trebuie adăugat cel puțin un element însoțitor.";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const calculateTotalAndCondition = () => {
    const materialValues = Object.values(selectedTask.materials || {}).map(
      (value) => {
        const parsed = parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : 0;
      }
    );
    const positiveValues = materialValues.filter((value) => value > 0);
    const total = positiveValues.reduce((acc, curr) => acc + curr, 0);
    const numberOfPositiveFields = positiveValues.length;
    const average =
      numberOfPositiveFields > 0 ? total / numberOfPositiveFields : 0;
    const lowestScore =
      numberOfPositiveFields > 0 ? Math.min(...positiveValues) : null;
    let condition;
    if (average === 10) {
      condition = "Nou";
    } else if (average < 10 && average >= 9) {
      condition = "Excelent";
    } else if (average < 9 && average >= 8) {
      condition = "Foarte Bun";
    } else if (average < 8 && average >= 7) {
      condition = "Bun";
    } else if (average < 7) {
      condition = "Rezonabil";
    } else {
      condition = "N/A";
    }
    return {
      total: average.toFixed(2),
      condition,
      lowestScore,
    };
  };

  // Returnează componenta cu un mesaj de eroare dacă issueId nu este definit
  if (!issueId) {
    return (
      <Box sx={{ width: "100%", padding: 2 }}>
        <Alert severity="error">Eroare: issueId este undefined sau null.</Alert>
      </Box>
    );
  }

  // Dacă datele taskului nu sunt încă încărcate, afișează un spinner de încărcare
  if (!selectedTask) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ padding: 2 }}>
          <TextField
            label="Summary"
            fullWidth
            margin="normal"
            InputProps={{
              readOnly: true,
            }}
            value={selectedTask?.summary || ""}
          />
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Preț negociat"
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                }}
                value={`${selectedTask?.entryPrice || ""} RON`}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Data de intrare"
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                }}
                value={
                  selectedTask?.entryDate
                    ? new Date(selectedTask.entryDate).toLocaleDateString()
                    : ""
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Categorie Prod"
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                }}
                value={selectedTask?.productCategory || "N/A"}
                sx={{ "& .MuiInputBase-input": { color: "green" } }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Nume și Prenume"
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                }}
                value={selectedTask?.fullName || ""}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Email"
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                }}
                value={selectedTask?.email || ""}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Telefon"
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                }}
                value={selectedTask?.phone || ""}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ marginTop: 2 }}>
            Autentificare
          </Typography>
          <FormControl fullWidth error={Boolean(errors.authenticity)}>
            <InputLabel id="authenticity-select-label">
              Articolul este autentic?
            </InputLabel>
            <Select
              labelId="authenticity-select-label"
              id="authenticity-select"
              value={selectedTask.authenticity || ""}
              error={Boolean(errors.authenticity)}
              helperText={errors.authenticity || ""}
              onChange={(event) => {
                const updatedTaskForAuthenticity = {
                  ...selectedTask,
                  authenticity: event.target.value,
                };
                setSelectedTask(updatedTaskForAuthenticity);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  authenticity: undefined,
                }));
              }}
            >
              <MenuItem value="DA">DA</MenuItem>
              <MenuItem value="NU">NU</MenuItem>
            </Select>
          </FormControl>

          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{ marginTop: 4 }}
          >
            Inspecția materialelor
          </Typography>

          {selectedTask.productCategory === "Ceasuri" ? (
            <Grid container spacing={2}>
              {["Cadran", "Bratara-Curea", "Geam", "Spate"].map(
                (label, index) => (
                  <Grid item xs={3} key={index}>
                    <TextField
                      label={label}
                      type="text"
                      value={
                        selectedTask.materials
                          ? selectedTask.materials[
                              materialLabelsToKeys[label]
                            ] || ""
                          : ""
                      }
                      onChange={handleMaterialChange(
                        materialLabelsToKeys[label]
                      )}
                      fullWidth
                      error={Boolean(errors[materialLabelsToKeys[label]])}
                      helperText={errors[materialLabelsToKeys[label]]}
                    />
                  </Grid>
                )
              )}
            </Grid>
          ) : selectedTask.productCategory === "Bijuterii" ? (
            <Grid container spacing={2}>
              {["Suprafețe", "Pietre"].map((label, index) => (
                <Grid item xs={6} key={index}>
                  <TextField
                    label={label}
                    type="text"
                    value={
                      selectedTask.materials
                        ? selectedTask.materials[materialLabelsToKeys[label]] ||
                          ""
                        : ""
                    }
                    onChange={handleMaterialChange(materialLabelsToKeys[label])}
                    fullWidth
                    error={Boolean(errors[materialLabelsToKeys[label]])}
                    helperText={errors[materialLabelsToKeys[label]]}
                  />
                </Grid>
              ))}
            </Grid>
          ) : selectedTask.productCategory === "Genti" ||
            selectedTask.productCategory === "Imbracaminte" ? (
            <Grid container spacing={2}>
              {["Exterior", "Interior", "Forma", "Hardware"].map(
                (label, index) => (
                  <Grid item xs={3} key={index}>
                    <TextField
                      label={label}
                      type="text"
                      value={
                        selectedTask.materials
                          ? selectedTask.materials[
                              materialLabelsToKeys[label]
                            ] || ""
                          : ""
                      }
                      onChange={handleMaterialChange(
                        materialLabelsToKeys[label]
                      )}
                      fullWidth
                      error={Boolean(errors[materialLabelsToKeys[label]])}
                      helperText={errors[materialLabelsToKeys[label]]}
                    />
                  </Grid>
                )
              )}
            </Grid>
          ) : selectedTask.productCategory === "Incaltaminte" ? (
            <Grid container spacing={2}>
              {["Exterior", "Interior", "Forma", "Talpa", "Cute"].map(
                (label, index) => (
                  <Grid item xs={3} key={index}>
                    <TextField
                      label={label}
                      type="number"
                      value={
                        selectedTask.materials
                          ? selectedTask.materials[
                              materialLabelsToKeys[label]
                            ] || ""
                          : ""
                      }
                      onChange={handleMaterialChange(
                        materialLabelsToKeys[label]
                      )}
                      fullWidth
                      error={Boolean(errors[materialLabelsToKeys[label]])}
                      helperText={errors[materialLabelsToKeys[label]]}
                    />
                  </Grid>
                )
              )}
            </Grid>
          ) : null}

          <Alert severity="info" sx={{ marginTop: 2 }}>
            Notă: 1-10 (1 fiind neacceptat, 10 fiind maxim)
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ marginTop: 4 }}>
            Funcționalitatea și designul
          </Typography>

          {selectedTask.productCategory === "Bijuterii" ? (
            <Grid container spacing={2}>
              {["Finisaje", "Pietre"].map((label, index) => (
                <Grid item xs={6} key={index}>
                  <TextField
                    label={label}
                    type="text"
                    value={
                      selectedTask.materials
                        ? selectedTask.materials[materialLabelsToKeys[label]] ||
                          ""
                        : ""
                    }
                    onChange={handleMaterialChange(materialLabelsToKeys[label])}
                    fullWidth
                    error={Boolean(errors[materialLabelsToKeys[label]])}
                    helperText={errors[materialLabelsToKeys[label]]}
                  />
                </Grid>
              ))}
            </Grid>
          ) : selectedTask.productCategory === "Ceasuri" ? (
            <Grid container spacing={2}>
              {["Finisaje", "Mecanism"].map((label, index) => (
                <Grid item xs={6} key={index}>
                  <TextField
                    label={label}
                    type="text"
                    value={
                      selectedTask.materials
                        ? selectedTask.materials[materialLabelsToKeys[label]] ||
                          ""
                        : ""
                    }
                    onChange={handleMaterialChange(materialLabelsToKeys[label])}
                    fullWidth
                    error={Boolean(errors[materialLabelsToKeys[label]])}
                    helperText={errors[materialLabelsToKeys[label]]}
                  />
                </Grid>
              ))}
            </Grid>
          ) : selectedTask.productCategory === "Accesorii" ? (
            <Grid container spacing={2} sx={{ marginBottom: 2 }}>
              <Grid item xs={4}>
                <TextField
                  required
                  label="Finisaje"
                  type="text"
                  value={
                    selectedTask.materials
                      ? selectedTask.materials[
                          materialLabelsToKeys["Finisaje"]
                        ] || ""
                      : ""
                  }
                  onChange={handleMaterialChange(
                    materialLabelsToKeys["Finisaje"]
                  )}
                  fullWidth
                  error={Boolean(errors[materialLabelsToKeys["Finisaje"]])}
                  helperText={errors[materialLabelsToKeys["Finisaje"]]}
                />
              </Grid>
            </Grid>
          ) : selectedTask.productCategory === "Incaltaminte" ? (
            <Grid container spacing={2} sx={{ marginBottom: 2 }}>
              <Grid item xs={12}>
                <TextField
                  required
                  label="Cute"
                  type="text"
                  value={
                    selectedTask.materials
                      ? selectedTask.materials[materialLabelsToKeys["Cute"]] ||
                        ""
                      : ""
                  }
                  onChange={handleMaterialChange(materialLabelsToKeys["Cute"])}
                  fullWidth
                  error={Boolean(errors[materialLabelsToKeys["Cute"]])}
                  helperText={errors[materialLabelsToKeys["Cute"]]}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2} sx={{ marginBottom: 2 }}>
              {["Fermoare-Inchideri", "Finisaje", "Mirosuri"].map(
                (label, index) => (
                  <Grid item xs={4} key={index}>
                    <TextField
                      required
                      label={label}
                      type="text"
                      value={
                        selectedTask.materials
                          ? selectedTask.materials[
                              materialLabelsToKeys[label]
                            ] || ""
                          : ""
                      }
                      onChange={handleMaterialChange(
                        materialLabelsToKeys[label]
                      )}
                      fullWidth
                      error={Boolean(errors[materialLabelsToKeys[label]])}
                      helperText={errors[materialLabelsToKeys[label]]}
                    />
                  </Grid>
                )
              )}
            </Grid>
          )}

          <Typography variant="h6" gutterBottom sx={{ marginTop: 4 }}>
            Ambalajul și accesoriile
          </Typography>
          <Grid container spacing={2}>
            {["Cutie", "Accesorii"].map((label, index) => (
              <Grid item xs={6} key={index}>
                <TextField
                  required
                  label={label}
                  value={
                    selectedTask.materials
                      ? selectedTask.materials[label.toLowerCase()] || ""
                      : ""
                  }
                  onChange={(event) => {
                    const prop = label.toLowerCase();
                    const updatedMaterials = {
                      ...selectedTask.materials,
                      [prop]: event.target.value,
                    };

                    setSelectedTask({
                      ...selectedTask,
                      materials: updatedMaterials,
                    });
                  }}
                  error={Boolean(errors[label.toLowerCase()])}
                  helperText={errors[label.toLowerCase()]}
                  fullWidth
                />
              </Grid>
            ))}
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ marginTop: 4 }}>
            Istoricul și proveniența produsulul
          </Typography>
          {selectedTask.productCategory === "Bijuterii" ? (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="eu-marking-label">Marcaj EU</InputLabel>
                  <Select
                    labelId="eu-marking-label"
                    id="eu-marking-select"
                    value={selectedTask.euMarking || ""}
                    label="Marcaj EU"
                    onChange={(e) => {
                      setSelectedTask({
                        ...selectedTask,
                        euMarking: e.target.value,
                      });
                    }}
                  >
                    <MenuItem value="DA">DA</MenuItem>
                    <MenuItem value="NU">NU</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="gemological-certificate-label">
                    Certificat Gemologic
                  </InputLabel>
                  <Select
                    labelId="gemological-certificate-label"
                    id="gemological-certificate-select"
                    value={selectedTask.gemologicalCertificate || ""}
                    label="Certificat Gemologic"
                    onChange={(e) =>
                      setSelectedTask({
                        ...selectedTask,
                        gemologicalCertificate: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="DA">DA</MenuItem>
                    <MenuItem value="NU">NU</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2} alignItems="center">
              {selectedTask.productCategory !== "Imbracaminte" && (
                <Grid item xs={6}>
                  <FormControl
                    fullWidth
                    sx={{ mb: 2 }}
                    error={Boolean(errors.Repainted)}
                  >
                    <InputLabel id="repainted-label">
                      {selectedTask.productCategory === "Ceasuri"
                        ? "Polisat"
                        : "Revopsit"}
                    </InputLabel>
                    <Select
                      labelId="repainted-label"
                      id="repainted-select"
                      value={selectedTask.repainted || ""}
                      label={
                        selectedTask.productCategory === "Ceasuri"
                          ? "Polisat"
                          : "Revopsit"
                      }
                      onChange={handleRepaintedChange}
                    >
                      <MenuItem value="DA">DA</MenuItem>
                      <MenuItem value="NU">NU</MenuItem>
                    </Select>
                    {errors.Repainted && (
                      <FormHelperText>{errors.Repainted}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  error={Boolean(errors.Repaired)}
                  sx={{ mb: 2 }}
                >
                  <InputLabel id="repaired-label">Reparat</InputLabel>
                  <Select
                    labelId="repaired-label"
                    id="repaired-select"
                    value={selectedTask.repaired || ""}
                    label="Reparat"
                    onChange={handleRepairedChange}
                  >
                    <MenuItem value="DA">DA</MenuItem>
                    <MenuItem value="NU">NU</MenuItem>
                  </Select>
                  {errors.Repaired && (
                    <FormHelperText>{errors.Repaired}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          )}

          <Typography variant="h6" gutterBottom sx={{ marginTop: 4 }}>
            Nota Finală
          </Typography>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={10}>
              <TextField
                label="A venit însoțit cu:"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                error={Boolean(errors.accompaniedItems)}
              />
            </Grid>
            <Grid item xs={2}>
              <IconButton onClick={handleAddItem}>
                <AddCircleOutlineIcon />
              </IconButton>
            </Grid>
          </Grid>
          {Array.isArray(accompaniedItems) &&
            accompaniedItems.map((item, index) => (
              <Grid container spacing={2} alignItems="center" key={index}>
                <Grid item xs={10}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={item}
                    sx={{ mb: 2 }}
                    onChange={handleItemChange(index)}
                    error={Boolean(errors.accompaniedItems)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton onClick={() => handleDeleteItem(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          {errors.accompaniedItems && (
            <FormHelperText error sx={{ ml: 2 }}>
              {errors.accompaniedItems}
            </FormHelperText>
          )}

          <Box
            sx={{
              position: "relative",
              display: "inline-block",
              mb: 2,
              width: "100%",
            }}
          >
            <FormControl
              fullWidth
              error={Boolean(errors.priceOption)}
              sx={{ width: "100%" }}
            >
              <InputLabel id="price-option-label">Preț</InputLabel>
              <Select
                labelId="price-option-label"
                id="price-option"
                value={priceOption}
                label="Preț"
                onChange={handlePriceOptionChange}
              >
                <MenuItem value="Cel agreat initial">
                  Cel agreat inițial
                </MenuItem>
                <MenuItem value="Propunere pret nou">
                  Propunere preț nou
                </MenuItem>
              </Select>
              {errors.priceOption && (
                <FormHelperText>{errors.priceOption}</FormHelperText>
              )}
            </FormControl>
            {priceOption === "Propunere pret nou" && (
              <TextField
                error={Boolean(errors.newPrice)}
                helperText={errors.newPrice}
                label="Preț Nou"
                type="number"
                value={newPrice}
                onChange={handleNewPriceChange}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">RON</InputAdornment>
                  ),
                  inputProps: { min: 0, pattern: "\\d*" },
                }}
              />
            )}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 40,
                transform: "translate(50%, -50%)",
                bgcolor: "green",
                color: "white",
                px: 2,
                py: 0.5,
                borderRadius: "16px",
                fontSize: "0.75rem",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "24px",
              }}
            >
              {selectedTask.entryPrice} RON
            </Box>
          </Box>

          <TextField
            label="Observații inspector de calitate"
            multiline
            rows={4}
            value={selectedTask.Observations || ""}
            onChange={handleObservationChange}
            fullWidth
            sx={{ marginBottom: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={rephraseObservations}
                    edge="end"
                    sx={{ color: "purple" }}
                  >
                    {isProcessing ? (
                      <CircularProgress size={24} />
                    ) : (
                      <AssistantSharpIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(errors.Observations)}
            helperText={errors.Observations}
          />

          <Grid container spacing={2}>
            {QC_IMAGE_SLOT_INDEXES.map((index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ position: "relative" }}>
                <Button variant="outlined" component="label" fullWidth>
                  {`Încarcă Imagine ${index + 1}`}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange(index)}
                  />
                </Button>
                {imageFiles[index] && imageFiles[index].previewUrl && (
                  <Box sx={{ position: "relative", mt: 1, width: "100%" }}>
                    <img
                      src={imageFiles[index].previewUrl}
                      alt={`Preview ${index + 1}`}
                      style={{
                        width: "100%",
                        maxHeight: 240,
                        objectFit: "contain",
                      }}
                    />
                    <IconButton
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        color: "red",
                        zIndex: 1,
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>

          <FormControl
            fullWidth
            sx={{ marginTop: 2, marginBottom: 2 }}
            error={Boolean(errors.Inspector)}
          >
            <InputLabel id="inspector-label">Inspector</InputLabel>
            <Select
              labelId="inspector-label"
              id="inspector-select"
              value={selectedTask.inspector || ""}
              label="Inspector"
              onChange={(e) => {
                setSelectedTask({ ...selectedTask, inspector: e.target.value });
              }}
            >
              {["Alexandra N.", "Ciprian B.", "Alina B.", "Mirela B."].map(
                (name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                )
              )}
            </Select>
            {errors.Inspector && (
              <FormHelperText>{errors.Inspector}</FormHelperText>
            )}
          </FormControl>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
              mb: 2,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <Alert variant="outlined" severity="info">
                  TOTAL: {calculateTotalAndCondition().total}
                </Alert>
              </Grid>
              <Grid item xs={6}>
                <Alert variant="outlined" severity="info">
                  Condiție: {calculateTotalAndCondition().condition}
                </Alert>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              mt: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Button
                variant="outlined"
                onClick={() => handleSubmitDraft("Draft")}
                sx={{ marginRight: 2 }}
                disabled={selectedTask?.status === "Done"}
              >
                Ciorna
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => handleSubmit("Done")}
                sx={{ backgroundColor: "#000", color: "#fff" }}
                disabled={selectedTask?.status === "Done"}
              >
                Salvează & Trimite la Client
              </Button>
            </Box>

            {/* Mesaje de feedback pentru email, SMS și JIRA */}
            {emailStatus && emailStatus.message && (
              <Alert
                severity={emailStatus.severity}
                sx={{ width: "100%", mb: 1 }}
              >
                {emailStatus.message}
              </Alert>
            )}

            {smsStatus && smsStatus.message && (
              <Alert
                severity={smsStatus.severity}
                sx={{ width: "100%", mb: 1 }}
              >
                {smsStatus.message}
              </Alert>
            )}

            {jiraStatus && jiraStatus.message && (
              <Alert
                severity={jiraStatus.severity}
                sx={{ width: "100%", mb: 1 }}
              >
                {jiraStatus.message}
              </Alert>
            )}

            {sellNowStatus && sellNowStatus.message && (
              <Alert
                severity={sellNowStatus.severity}
                sx={{ width: "100%", mb: 1 }}
              >
                {sellNowStatus.message}
              </Alert>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default CreareRaport;
